import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import type { FillInBlankContentBlock } from "../../types";
import { QuestionHeader } from "./question-header";
import { useInteractiveSubmit } from "../../hooks/useInteractiveSubmit";
import { useOptionTransfer } from "../../hooks/useOptionTransfer";
import {
  interactiveRootSx,
  optionsPoolSx,
  useInteractiveStyles,
} from "./styles";
import { OptionTransferTouchGhost } from "./option-transfer-touch-ghost";
import { useShuffledItems } from "../../hooks/useShuffledItems";
import type { AssessmentInteractiveModeProps } from "./assessment-mode";
import { notifyOfflineActionBlocked } from "../../utils/internetConnection";

const TEMPLATE_TOKEN = /\{\{(\w+)\}\}/g;
const EMPTY_ANSWERS: Record<string, string> = {};

const normalize = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const getBlankCorrectMapFromAnswers = (
  answers: Record<string, string> | undefined,
  userAnswers: Record<string, string>,
  optionById: Map<string, { id: string; text: string }>,
): Record<string, boolean> => {
  if (!answers) return {};
  const result: Record<string, boolean> = {};
  Object.entries(answers).forEach(([blankId, expectedValue]) => {
    const userOptionId = userAnswers[blankId];
    if (!userOptionId) return;
    const userText = optionById.get(userOptionId)?.text;
    const expected = normalize(expectedValue);
    result[blankId] =
      normalize(userOptionId) === expected || normalize(userText) === expected;
  });
  return result;
};

interface FillInBlankBlockProps extends AssessmentInteractiveModeProps {
  block: FillInBlankContentBlock;
  blockIndex: number;
  messageId: string;
  userId: string;
  tabId: string;
  onQuizAnswered?: () => void;
}

export const FillInBlankBlock: React.FC<FillInBlankBlockProps> = ({
  block,
  blockIndex,
  messageId,
  userId,
  tabId,
  onQuizAnswered,
  hideSubmitButton = false,
  hideFeedback = false,
  hideResultHighlight = false,
  isAssessmentMode = false,
  submitGetterRef,
  onCompletionChange,
}) => {
  const interactiveStyles = useInteractiveStyles(isAssessmentMode);
  const disabled = block.state === "disable";
  const revealResults = disabled && !hideResultHighlight;
  const containerRef = useRef<HTMLDivElement>(null);
  const { submit, isSubmitting, hasSubmitted, isBrowserLocked, isOffline } =
    useInteractiveSubmit(onQuizAnswered);
  const isInteractionLocked =
    isSubmitting || hasSubmitted || isBrowserLocked || isOffline;

  const transfer = useOptionTransfer({
    disabled: disabled || hasSubmitted,
    isBrowserLocked,
    isOffline,
    containerRef,
  });

  const [draftAnswers, setDraftAnswers] = useState<Record<string, string>>(
    () => block.user_answers ?? {},
  );

  useEffect(() => {
    setDraftAnswers(block.user_answers ?? {});
  }, [block.user_answers, block.state, tabId]);

  const answers = useMemo(() => {
    if (disabled) {
      return block.user_answers ?? EMPTY_ANSWERS;
    }
    return draftAnswers;
  }, [disabled, block.user_answers, draftAnswers]);

  const optionById = useMemo(
    () => new Map(block.options.map((o) => [o.id, o])),
    [block.options],
  );

  const blankIds = useMemo(() => {
    const ids: string[] = [];
    const re = new RegExp(TEMPLATE_TOKEN.source, "g");
    let match: RegExpExecArray | null;
    while ((match = re.exec(block.template)) !== null) {
      ids.push(match[1]);
    }
    if (ids.length === 0 && block.blanks.length) {
      return block.blanks.map((b) => b.id);
    }
    return ids;
  }, [block.template, block.blanks]);

  const shuffledOptions = useShuffledItems(
    block.options,
    messageId,
    blockIndex,
    tabId,
  );

  const usedOptionIds = useMemo(
    () => new Set(Object.values(answers).filter(Boolean)),
    [answers],
  );

  const poolOptions = useMemo(() => {
    if (disabled) return block.options;
    return shuffledOptions.filter((o) => !usedOptionIds.has(o.id));
  }, [block.options, shuffledOptions, disabled, usedOptionIds]);

  const allFilled = blankIds.every((id) => answers[id]);
  const isSubmitDisabled = !allFilled || isInteractionLocked;

  useEffect(() => {
    onCompletionChange?.(allFilled);
  }, [allFilled, onCompletionChange]);

  useEffect(() => {
    if (!submitGetterRef) return;
    submitGetterRef.current = () => {
      if (!allFilled) return null;
      return {
        interactionType: "fill_in_blank",
        payload: { user_answers: draftAnswers },
      };
    };
    return () => {
      submitGetterRef.current = null;
    };
  }, [submitGetterRef, allFilled, draftAnswers]);

  const placeOption = useCallback(
    (blankId: string, optionId: string) => {
      if (disabled) return;
      if (isOffline) {
        notifyOfflineActionBlocked();
        return;
      }
      setDraftAnswers((prev) => {
        const next = { ...prev };
        const prevOption = next[blankId];
        if (prevOption === optionId) {
          delete next[blankId];
          return next;
        }
        Object.keys(next).forEach((key) => {
          if (next[key] === optionId) delete next[key];
        });
        next[blankId] = optionId;
        return next;
      });
    },
    [disabled, isOffline],
  );

  const clearBlank = useCallback(
    (blankId: string) => {
      if (disabled) return;
      if (isOffline) {
        notifyOfflineActionBlocked();
        return;
      }
      setDraftAnswers((prev) => {
        if (!prev[blankId]) return prev;
        const next = { ...prev };
        delete next[blankId];
        return next;
      });
    },
    [disabled, isOffline],
  );

  const handleBlankClick = useCallback(
    (
      blankId: string,
      optionId: string | undefined,
      placeSelected: () => void,
    ) => {
      if (disabled) return;
      if (isOffline) {
        notifyOfflineActionBlocked();
        return;
      }
      if (transfer.selectedOptionId) {
        placeSelected();
        return;
      }
      if (optionId) {
        clearBlank(blankId);
      }
    },
    [disabled, isOffline, transfer.selectedOptionId, clearBlank],
  );

  const handleSubmit = async () => {
    if (!allFilled || disabled || hideSubmitButton || isInteractionLocked) {
      return;
    }
    await submit({
      messageId,
      userId,
      tabId,
      interactionType: "fill_in_blank",
      blockIndex,
      data: block.data,
      payload: { user_answers: draftAnswers },
    });
  };

  const answersCorrectMap = useMemo(
    () => getBlankCorrectMapFromAnswers(block.answers, answers, optionById),
    [block.answers, answers, optionById],
  );

  const getBlankResult = (blankId: string): "correct" | "incorrect" | undefined => {
    if (!revealResults) return undefined;
    if (typeof answersCorrectMap[blankId] === "boolean") {
      return answersCorrectMap[blankId] ? "correct" : "incorrect";
    }
    return undefined;
  };

  const poolHint = transfer.getPoolHint(
    "Drag words into blanks. Tap a filled blank to remove it.",
    "Tap a filled blank to remove — or tap/drag a word into a blank",
  );

  const renderTemplate = () => {
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    const re = new RegExp(TEMPLATE_TOKEN.source, "g");
    let match: RegExpExecArray | null;
    let key = 0;

    while ((match = re.exec(block.template)) !== null) {
      const before = block.template.slice(lastIndex, match.index);
      if (before) parts.push(<span key={`t-${key++}`}>{before}</span>);

      const blankId = match[1];
      const optionId = answers[blankId];
      const optionText = optionId ? optionById.get(optionId)?.text : null;
      const dropHandlers = transfer.getDropTargetHandlers(blankId, (id) =>
        placeOption(blankId, id),
      );
      const placeSelected = dropHandlers.onClick;

      parts.push(
        <Box
          key={`b-${blankId}`}
          component="span"
          sx={
            [
              interactiveStyles.getBlankSlotSx(
                !!optionText,
                getBlankResult(blankId),
                !disabled,
              ),
              transfer.isDropTargetActive(blankId)
                ? interactiveStyles.dropTargetActiveSx
                : {},
            ] as SxProps<Theme>
          }
          {...dropHandlers}
          onClick={() => handleBlankClick(blankId, optionId, placeSelected)}
        >
          {optionText ?? (disabled ? "—" : " ")}
        </Box>,
      );
      lastIndex = match.index + match[0].length;
    }

    const tail = block.template.slice(lastIndex);
    if (tail) parts.push(<span key={`t-${key++}`}>{tail}</span>);
    return parts;
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        ...interactiveRootSx,
        ...(transfer.isDragActive
          ? { overflow: "hidden", touchAction: "none" }
          : {}),
      }}
    >
      <QuestionHeader
        title={block.title}
        instruction={block.instruction}
        styles={interactiveStyles}
      />

      <Box sx={interactiveStyles.cardSx}>
        <Box sx={interactiveStyles.sentenceBoxSx}>{renderTemplate()}</Box>
      </Box>

      {!disabled ? (
        <Box>
          <Typography sx={interactiveStyles.poolLabelSx}>{poolHint}</Typography>
          <Box sx={optionsPoolSx}>
            {poolOptions.map((option) => {
              const poolHandlers = transfer.getPoolOptionHandlers(
                option.id,
                option.text,
              );
              return (
                <Box
                  key={option.id}
                  {...poolHandlers}
                  sx={interactiveStyles.getOptionChipSx(
                    transfer.isDragging(option.id),
                    transfer.getOptionHighlight(option.id),
                  )}
                >
                  {option.text}
                </Box>
              );
            })}
          </Box>
        </Box>
      ) : null}

      {block.feedback && disabled && !hideFeedback ? (
        <Typography sx={{ fontSize: "0.875rem", color: "#374151" }}>
          {block.feedback}
        </Typography>
      ) : null}

      {!disabled && !hideSubmitButton ? (
        <Button
          variant="contained"
          disableElevation
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
          sx={interactiveStyles.submitButtonSx(isSubmitDisabled)}
        >
          {isSubmitting ? "Submitting..." : "Submit Answer"}
        </Button>
      ) : null}

      {transfer.touchGhost ? (
        <OptionTransferTouchGhost
          text={transfer.touchGhost.text}
          x={transfer.touchGhost.relativeX}
          y={transfer.touchGhost.relativeY}
          sx={interactiveStyles.touchDragGhostSx}
        />
      ) : null}
    </Box>
  );
};
